import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RecordKPRConstants } from '../record-kpr-constants';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiConstants } from '../../shared/constants/api-constants';
import { ChatRequestDto } from '../models/chat-request-dto';
import { ChatResponseDto } from '../models/chat-response-dto';
import { PromptSessionRequestDto, PromptSessionStatus } from '../models/prompt-session-request-dto';

@Injectable({
    providedIn: 'root'
})
export class RecordKPRService {
    private static readonly clientWindowHeaderName = 'X-RecordKpr-Client-Window-Id';
    private static readonly clientWindowStorageKey = 'recordkpr_client_window_id';
    private static readonly promptSessionHeartbeatIntervalMs = 60000;
    private static readonly promptSessionTimeoutMs = 10 * 60 * 1000;
    private static readonly promptSessionEndpoint = `${ApiConstants.RecordKPR_Chat_Api}/prompt-session`;

    private sessionId: string = crypto.randomUUID();
    private sessionExchangeIndex: number = 1;
    private hasPendingNewSessionMarker: boolean = true;
    private readonly clientWindowId: string;
    private promptSessionId: string = crypto.randomUUID();
    private promptSessionCorrelationId: string | null = null;
    private promptSessionStartedAtMs: number | null = null;
    private promptSessionQueryCount: number = 0;
    private promptSessionHeartbeatTimerId: number | null = null;
    private promptSessionTimeoutTimerId: number | null = null;
    private promptSessionActive: boolean = false;

    constructor(private httpClient: HttpClient) {
        this.clientWindowId = this.initializeClientWindowId();
    }

    startNewSession(): void {
        this.sessionId = crypto.randomUUID();
        this.sessionExchangeIndex = 1;
        this.hasPendingNewSessionMarker = true;
    }

    startPromptSession(): void {
        this.clearPromptSessionTimers();
        this.promptSessionId = crypto.randomUUID();
        this.promptSessionCorrelationId = null;
        this.promptSessionStartedAtMs = Date.now();
        this.promptSessionQueryCount = 0;
        this.promptSessionActive = true;

        this.publishPromptSession('opened');
        this.schedulePromptSessionHeartbeat();
        this.schedulePromptSessionTimeout();
    }

    recordPromptSessionQuery(): void {
        if (!this.promptSessionActive) {
            return;
        }

        this.promptSessionQueryCount += 1;
        this.schedulePromptSessionTimeout();
    }

    setPromptSessionCorrelationId(correlationId?: string): void {
        if (!this.promptSessionActive) {
            return;
        }

        this.promptSessionCorrelationId = correlationId?.trim() || null;
    }

    closePromptSession(): void {
        this.endPromptSession('closed');
    }

    closePromptSessionForPageHide(): void {
        this.endPromptSession('closed', true);
    }

    timeoutPromptSession(): void {
        this.endPromptSession('timeout');
    }

    getCurrentSessionId(): string {
        return this.sessionId;
    }

    consumeNextExchangeIndex(): number {
        return this.sessionExchangeIndex++;
    }

    consumeIsNewSessionMarker(): boolean {
        const isNewSession = this.hasPendingNewSessionMarker;
        this.hasPendingNewSessionMarker = false;
        return isNewSession;
    }

    sendMessage(request: ChatRequestDto): Observable<ChatResponseDto> {
        const headers = new HttpHeaders().set(RecordKPRService.clientWindowHeaderName, this.clientWindowId);
        return this.httpClient.post<ChatResponseDto>(ApiConstants.RecordKPR_Chat_Api, request, { headers }).pipe(
            map((resp: ChatResponseDto) => ({
                response: resp?.response ?? RecordKPRConstants.Agent_Busy_Message,
                correlation_id: resp?.correlation_id
            }))
        );
    }

    recordDocCited(request: { correlationId: string; sessionId: string; citedURI: string; citedAt: string }): Observable<void> {
        const headers = new HttpHeaders().set(RecordKPRService.clientWindowHeaderName, this.clientWindowId);
        return this.httpClient.post<void>(`${ApiConstants.RecordKPR_Chat_Api}/doc-cited`, request, { headers });
    }

    recordPromptSession(request: PromptSessionRequestDto): Observable<void> {
        const headers = new HttpHeaders().set(RecordKPRService.clientWindowHeaderName, this.clientWindowId);
        return this.httpClient.post<void>(RecordKPRService.promptSessionEndpoint, request, { headers });
    }

    recordKPRViewAuthorization(): Observable<boolean> {
        return this.httpClient.get<boolean>(ApiConstants.RecordKPR_View_Authorize_Api);
    }

    private initializeClientWindowId(): string {
        const existing = sessionStorage.getItem(RecordKPRService.clientWindowStorageKey);
        if (existing) {
            return existing;
        }

        const created = crypto.randomUUID();
        sessionStorage.setItem(RecordKPRService.clientWindowStorageKey, created);
        return created;
    }

    private publishPromptSession(status: PromptSessionStatus, preferBeacon: boolean = false): void {
        if (!this.promptSessionActive) {
            return;
        }

        const request = this.buildPromptSessionRequest(status);

        if (preferBeacon && this.trySendPromptSessionBeacon(request)) {
            return;
        }

        this.recordPromptSession(request).subscribe({
            error: () => {
                // PromptSession telemetry is best-effort and must never block the UI.
            }
        });
    }

    private buildPromptSessionRequest(status: PromptSessionStatus): PromptSessionRequestDto {
        return {
            promptSessionId: this.promptSessionId,
            correlationId: this.promptSessionCorrelationId,
            sessionId: this.sessionId,
            status,
            timestamp: new Date().toISOString(),
            heartbeatIntervalMs: RecordKPRService.promptSessionHeartbeatIntervalMs,
            queryCount: this.promptSessionQueryCount,
            abandonedWithoutQuery: this.isPromptSessionAbandoned(status),
            sessionDurationMs: this.getPromptSessionDurationMs(status)
        };
    }

    private schedulePromptSessionHeartbeat(): void {
        this.promptSessionHeartbeatTimerId = window.setInterval(() => {
            if (!this.promptSessionActive) {
                return;
            }

            this.publishPromptSession('heartbeat');
        }, RecordKPRService.promptSessionHeartbeatIntervalMs);
    }

    private schedulePromptSessionTimeout(): void {
        if (this.promptSessionTimeoutTimerId !== null) {
            window.clearTimeout(this.promptSessionTimeoutTimerId);
        }

        this.promptSessionTimeoutTimerId = window.setTimeout(() => {
            this.timeoutPromptSession();
        }, RecordKPRService.promptSessionTimeoutMs);
    }

    private clearPromptSessionTimers(): void {
        if (this.promptSessionHeartbeatTimerId !== null) {
            window.clearInterval(this.promptSessionHeartbeatTimerId);
            this.promptSessionHeartbeatTimerId = null;
        }

        if (this.promptSessionTimeoutTimerId !== null) {
            window.clearTimeout(this.promptSessionTimeoutTimerId);
            this.promptSessionTimeoutTimerId = null;
        }
    }

    private endPromptSession(status: 'closed' | 'timeout', preferBeacon: boolean = false): void {
        if (!this.promptSessionActive) {
            return;
        }

        this.clearPromptSessionTimers();
        this.publishPromptSession(status, preferBeacon);
        this.resetPromptSessionState();
    }

    private trySendPromptSessionBeacon(request: PromptSessionRequestDto): boolean {
        if (typeof window === 'undefined' || typeof fetch !== 'function') {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                return navigator.sendBeacon(RecordKPRService.promptSessionEndpoint, JSON.stringify(request));
            }

            return false;
        }

        const OauthTokenProvider = (window as any)?.Master?.OauthTokenProvider;
        void this.sendPromptSessionWithAuthHeaders(
            request,
            OauthTokenProvider ? new OauthTokenProvider() : null
        );
        return true;
    }

    private async sendPromptSessionWithAuthHeaders(
        request: PromptSessionRequestDto,
        oauthTokenProvider: { getOauthToken: (url: string) => Promise<string> } | null
    ): Promise<void> {
        try {
            const accessToken = oauthTokenProvider
                ? await oauthTokenProvider.getOauthToken(RecordKPRService.promptSessionEndpoint)
                : undefined;
            const headers = this.buildPromptSessionHeaders(accessToken);

            await fetch(RecordKPRService.promptSessionEndpoint, {
                method: 'POST',
                headers,
                keepalive: true,
                credentials: 'same-origin',
                body: JSON.stringify(request)
            });
        } catch {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                navigator.sendBeacon(RecordKPRService.promptSessionEndpoint, JSON.stringify(request));
            }
        }
    }

    private buildPromptSessionHeaders(accessToken?: string): Record<string, string> {
        const currentUser = (window as any)?.Master?.currentUser;
        const puid = (sessionStorage.getItem('puid') ?? currentUser?.Puid ?? '').trim();
        const labSiteCode = currentUser?.labSiteCode ?? '';

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-User-Puid': puid.toUpperCase(),
            'X-User-LabsiteCode': labSiteCode
        };

        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        return headers;
    }

    private resetPromptSessionState(): void {
        this.promptSessionActive = false;
        this.promptSessionStartedAtMs = null;
        this.promptSessionQueryCount = 0;
        this.promptSessionId = crypto.randomUUID();
        this.promptSessionCorrelationId = null;
    }

    private getPromptSessionDurationMs(status: PromptSessionStatus): number | null {
        if (status === 'opened' || status === 'heartbeat' || this.promptSessionStartedAtMs === null) {
            return null;
        }

        return Math.max(Date.now() - this.promptSessionStartedAtMs, 0);
    }

    private isPromptSessionAbandoned(status: PromptSessionStatus): boolean {
        if (status === 'opened' || status === 'heartbeat') {
            return false;
        }

        return this.promptSessionQueryCount === 0;
    }
}
